import { useMemo } from "react";
import { getDMPrintingCostByFlyer, calculateDataCost } from "shared/campaign";
import { roundTo2 } from "utils/formators";
import { calculateTaxAndTotalCost } from "Hooks/useEDDMCostsCalculationV2/utils";
import { useQuery } from "react-query";
import { updateCampaignExtraData } from "../../../graphQL";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { ADM_FORMAT } from "campaign-builder/domain/constants";
import { getFormatsFor } from "campaign-builder/formats/getFormatsFor";

const DEFAULT_CAMPAIGN_COST_PREVIEW = {
  lines: [],
  subtotal: 0,
  tax: 0,
  total: 0,
  amountOff: 0,
  percentOff: 0,
  dataCost: 0,
};

export const useCampaignCostPreviewCalculation = ({
  format,
  dryRun = false,
}: {
  format?: ADM_FORMAT;
  /** When true, the calculation runs purely as a preview — skips the
   *  `updateCampaignExtraData` side effect so callers can compute a
   *  "what-if" total (e.g. the design-format-change modal needs the
   *  total under a hypothetical format without persisting it). */
  dryRun?: boolean;
} = {}) => {
  const { campaignData, activeAudienceDetails } = useCampaignData();
  const { admPrintingCosts, country } = useClientConfig();

  const flyersAmount = useMemo(
    () => activeAudienceDetails.reduce((acc, audience) => acc + audience.metadata.rowsAmount, 0),
    [activeAudienceDetails]
  );

  const amountOff = useMemo(() => {
    if (!campaignData) return 0;
    return campaignData.percentOff ? (campaignData.percentOff / 100) * campaignData.totalCosts : campaignData.amountOff;
  }, [campaignData?.percentOff, campaignData?.amountOff]);

  // An ADM campaign is created without a flyerType (unlike H2H, whose creation sends one), so
  // until the Design step is reached campaignData.flyerType is null. getDMPrintingCostByFlyer
  // returns 0 for a format it cannot find, which surfaced as "0" prices on every audience card
  // and in the Summary (PAD-31991). Resolve the format the way the Design step already does —
  // the catalog's isDefault entry, else its first — so the preview prices the format the
  // campaign will actually start on. US ADM defaults to POSTCARD_9x6, not the cheapest option.
  const effectiveFlyerType = useMemo(() => {
    if (format) return format;
    if (campaignData?.flyerType) return campaignData.flyerType;
    const formats = getFormatsFor("ADM", country?.code ?? "");
    return formats.find((f) => f.isDefault)?.value ?? formats[0]?.value;
  }, [format, campaignData?.flyerType, country?.code]);

  const costPerFlyer = useMemo(() => {
    if (!campaignData || !activeAudienceDetails || !admPrintingCosts || !country) return 0;
    return getDMPrintingCostByFlyer({
      printingCosts: admPrintingCosts,
      flyerType: effectiveFlyerType,
      flyersAmount,
    });
  }, [campaignData, activeAudienceDetails, admPrintingCosts, effectiveFlyerType, country]);

  const dataCost = useMemo(() => {
    if (!activeAudienceDetails || !admPrintingCosts) return 0;
    return calculateDataCost({
      audiences: activeAudienceDetails,
      printingCosts: admPrintingCosts,
    });
  }, [activeAudienceDetails, admPrintingCosts]);

  const calculateCampaignCostPreview = () => {
    if (!campaignData || !activeAudienceDetails || !admPrintingCosts || !country) return;
    const countryTaxRate = country?.countryDefaultSettings?.countryTaxRate || 0;
    const discountDetails = {
      amountOff: campaignData.amountOff,
      percentOff: campaignData.percentOff,
      stripeCouponCode: campaignData.stripeCouponCode,
    };
    const printingSubtotal = flyersAmount * costPerFlyer;
    const dataSubtotal = dataCost;
    const subtotal = printingSubtotal + dataSubtotal;
    const fixed2Subtotal = roundTo2(subtotal);
    const amountOff = roundTo2(
      discountDetails.percentOff ? (discountDetails.percentOff / 100) * fixed2Subtotal : discountDetails.amountOff || 0
    );

    const { tax, totalCost } = calculateTaxAndTotalCost({
      countryTaxRate,
      subtotal: fixed2Subtotal,
      amountOff: amountOff || null,
    });

    const lines = [
      {
        title: "Addressed Direct Mail",
        quantity: flyersAmount,
        price: fixed2Subtotal,
      },
    ];

    if (discountDetails.stripeCouponCode) {
      lines.push({
        title: `Coupon ${discountDetails.stripeCouponCode}`,
        quantity: 1,
        price: -amountOff,
      });
    }

    // Never persist a zero cost (PAD-32595). `flyersAmount` is derived from
    // `activeAudienceDetails`, which resolves the campaign's audiences against a
    // *paginated* client audience list and drops anything it cannot match. While
    // that list is still loading — or when the selected audience sits on a page
    // that has not been fetched — the list is empty, the whole calculation
    // collapses to 0, and this fire-and-forget write would overwrite a perfectly
    // good total_costs with 0. A campaign with no resolved audience has no
    // meaningful cost, so there is nothing worth persisting either way.
    if (!campaignData.isSubmitted && !dryRun && flyersAmount > 0) {
      updateCampaignExtraData({
        campaignId: campaignData.id,
        totalCosts: totalCost,
      });
    }

    return {
      lines,
      subtotal: fixed2Subtotal,
      tax,
      total: totalCost,
      amountOff,
      percentOff: discountDetails.percentOff,
      dataCost: roundTo2(dataSubtotal),
    };
  };

  const { data: campaignCostPreview = DEFAULT_CAMPAIGN_COST_PREVIEW, refetch: refetchCampaignCostPreview } = useQuery({
    queryKey: [
      "campaignCostPreview",
      campaignData?.id,
      campaignData?.stripeCouponCode,
      activeAudienceDetails.map((audience) => audience.id).join(","),
      // The resolved format, not the raw one: it is the value the calculation actually
      // uses, so keying on it avoids caching a result under an undefined format.
      effectiveFlyerType,
      country?.id,
      amountOff,
      dataCost,
      // The pricing inputs themselves. Without them a client pricing table that
      // lands after the audiences left the preview cached at $0 under an
      // unchanged key until an audience was added or removed.
      flyersAmount,
      costPerFlyer,
      // Segregate dryRun caches from live ones so a preview-only call
      // can't "poison" the cache and skip the persisted-write side
      // effect for a subsequent live call with the same other inputs.
      dryRun ? "dryRun" : "live",
    ],
    queryFn: () => {
      return calculateCampaignCostPreview();
    },
    // calculateCampaignCostPreview returns undefined until these load; react-query
    // would cache that as a result (shown as the $0 default) and never re-run it.
    enabled: !!campaignData && !!admPrintingCosts && !!country,
  });

  const submittedCampaignCostPreview = useMemo(() => {
    if (!campaignData || !campaignData.isSubmitted) return null;
    const lines = [
      {
        title: "Addressed Direct Mail",
        quantity: flyersAmount,
        price: roundTo2(campaignData.subtotal),
      },
    ];

    const submittedDataCost = campaignData.dataCost || 0;

    let amountOff = 0;
    if (campaignData.stripeCouponCode && (campaignData.amountOff || campaignData.percentOff)) {
      amountOff = roundTo2(campaignData.amountOff || (campaignData.percentOff! / 100) * campaignData.totalCosts);
      lines.push({
        title: `Coupon ${campaignData.stripeCouponCode}`,
        quantity: 1,
        price: -amountOff,
      });
    }
    return {
      lines: lines,
      subtotal: roundTo2(campaignData.subtotal),
      tax: roundTo2(campaignData.taxes ?? 0),
      total: roundTo2(campaignData.totalCosts),
      amountOff: amountOff,
      dataCost: roundTo2(submittedDataCost),
    };
  }, [campaignData]);

  return {
    campaignCostPreview:
      campaignData?.isSubmitted && submittedCampaignCostPreview ? submittedCampaignCostPreview : campaignCostPreview,
    refetchCampaignCostPreview,
    costPerFlyer,
    flyersAmount,
    dataCost,
  };
};
